import { NavigationContext } from "@-/com.npmjs.expo-router/react-navigation";
import { AppState } from "@-/com.npmjs.react-native";
import { useCallback, useContext, useRef, useSyncExternalStore } from "react";
import { VisualActivityContext } from "#visual-activity-context";

const getForegroundSnapshot = () =>
  AppState.currentState == null || AppState.currentState === "active";
const getServerSnapshot = () => true;
const subscribeForeground = (onChange: () => void) => {
  const subscription = AppState.addEventListener("change", onChange);
  return () => subscription.remove();
};

/** Stop visual work immediately on blur/background, while supporting isolated Storybook trees. */
export function useVisualActivity(): boolean {
  const contextActive = useContext(VisualActivityContext);
  const navigation = useContext(NavigationContext);
  // The focus the last `focus` / `blur` event reported while subscribed. Expo Router's navigation
  // emits `blur` while `navigation.isFocused()` still reports the route as focused, so reading only
  // `isFocused()` in the notification left a blurred screen visually active until some unrelated
  // rerender. `null` = no event observed yet, so fall back to `isFocused()`.
  const eventFocusRef = useRef<boolean | null>(null);
  const subscribeFocus = useCallback(
    (onChange: () => void) => {
      if (!contextActive || navigation === undefined) {
        return () => {};
      }
      eventFocusRef.current = null;
      const unsubscribeFocus = navigation.addListener("focus", () => {
        eventFocusRef.current = true;
        onChange();
      });
      const unsubscribeBlur = navigation.addListener("blur", () => {
        eventFocusRef.current = false;
        onChange();
      });
      return () => {
        unsubscribeFocus();
        unsubscribeBlur();
        // Events are not observed while unsubscribed, so a stale one must not outlive it.
        eventFocusRef.current = null;
      };
    },
    [contextActive, navigation],
  );
  // Mask the external-store snapshot itself. Masking only the returned value still rerenders
  // every hidden glyph when navigation focus changes, although its activity remains false.
  const getFocusSnapshot = useCallback(
    () => contextActive && (eventFocusRef.current ?? navigation?.isFocused() ?? true),
    [contextActive, navigation],
  );
  const focused = useSyncExternalStore(subscribeFocus, getFocusSnapshot, getServerSnapshot);
  const subscribeActiveForeground = useCallback(
    (onChange: () => void) => (contextActive ? subscribeForeground(onChange) : () => {}),
    [contextActive],
  );
  const getActiveForegroundSnapshot = useCallback(
    () => contextActive && getForegroundSnapshot(),
    [contextActive],
  );
  const foreground = useSyncExternalStore(
    subscribeActiveForeground,
    getActiveForegroundSnapshot,
    getServerSnapshot,
  );
  return contextActive && focused && foreground;
}
